import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Zap, Target, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';

const WHATSAPP_LINK = 'https://wa.me/523312949249?text=Quiero%20mi%20menú%20digital%20para%20mi%20negocio';

const features = [
  { id: 'feature-scan', text: 'El cliente escanea' },
  { id: 'feature-enter', text: 'Entra inmediatamente' },
  { id: 'feature-navigate', text: 'Navega fácil' },
  { id: 'feature-see', text: 'Ve lo importante' },
  { id: 'feature-decide', text: 'Decide rápido' },
  { id: 'feature-order', text: 'Pide más' },
];

export const SolutionImage = ({ inView }) => {
  const scaleAnimation = useMemo(() => ({
    animate: { scale: [1, 1.05, 1] },
    transition: { duration: 2, repeat: Infinity },
  }), []);

  return (
    <div className="relative">
      <div className="relative rounded-3xl overflow-hidden shadow-2xl">
        <img
          src="https://customer-assets.emergentagent.com/job_digital-menu-launch/artifacts/uag0uur8_IMAGEN%202.png"
          alt="Zappien SmartMenu Interface"
          className="w-full h-auto object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
      </div>
      
      <motion.div
        {...scaleAnimation}
        className="absolute top-6 left-6 glass-card px-4 py-3 rounded-xl border border-primary/30"
      >
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-[hsl(var(--cta-orange))]" />
          <span className="font-semibold text-foreground">Zappien SmartMenu™</span>
        </div>
      </motion.div>
    </div>
  );
};

export const SolutionContent = ({ inView }) => {
  const featureAnimation = useMemo(() => (index) => ({
    initial: { opacity: 0, x: -10 },
    animate: inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 },
    transition: { delay: 0.5 + index * 0.1 },
  }), [inView]);

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl sm:text-3xl font-bold mb-4">
          Por eso creamos <span className="text-primary">Zappien SmartMenu™</span>
        </h3>
        <p className="text-lg text-muted-foreground mb-6">
          Un sistema que convierte tu menú en una experiencia diseñada para vender.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="glass-card p-4 rounded-xl border border-destructive/30">
          <div className="text-sm text-muted-foreground mb-1">No es un</div>
          <div className="font-bold text-destructive line-through">PDF</div>
        </div>
        <div className="glass-card p-4 rounded-xl border border-destructive/30">
          <div className="text-sm text-muted-foreground mb-1">No es una</div>
          <div className="font-bold text-destructive line-through">Página básica</div>
        </div>
      </div>

      <div className="glass-card p-6 rounded-2xl border border-primary/30">
        <div className="flex items-center gap-2 mb-4">
          <Target className="w-6 h-6 text-primary" />
          <h4 className="text-xl font-bold">Es un sistema estratégico</h4>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {features.map((feature, index) => (
            <motion.div
              key={feature.id}
              {...featureAnimation(index)}
              className="flex items-center gap-2"
            >
              <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
              <span className="text-sm text-foreground">{feature.text}</span>
            </motion.div>
          ))}
        </div>
      </div>

      <Button
        size="lg"
        className="w-full bg-gradient-to-r from-[hsl(var(--cta-orange))] to-[hsl(var(--cta-orange-hover))] text-white hover:shadow-[0_0_30px_hsl(var(--cta-orange)/0.4)] transition-all duration-300"
        onClick={() => window.open(WHATSAPP_LINK, '_blank')}
      >
        Quiero mi menú que venda
      </Button>
    </div>
  );
};
